/**
 * Panel showing version history for a lesson prompt
 * Allows viewing previous versions and restoring them
 */

import { usePromptHistory } from '@/hooks/useLessonPrompts';
import { PromptHistoryEntry } from '@/types/lessonPrompt';
import { Button } from '@/components/ui/button';
import { History, RotateCcw, Loader2, Clock, User, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface PromptHistoryPanelProps {
  lessonId: string;
  onRestore?: () => void;
}

export function PromptHistoryPanel({ lessonId, onRestore }: PromptHistoryPanelProps) {
  const { history, loading, restoring, restoreFromHistory } = usePromptHistory(lessonId);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [restoreId, setRestoreId] = useState<string | null>(null);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleRestore = async () => {
    if (!restoreId) return;
    const result = await restoreFromHistory(restoreId);
    if (result) {
      setRestoreId(null);
      onRestore?.();
    }
  };

  const getChangeTypeLabel = (type: PromptHistoryEntry['changeType']) => {
    switch (type) {
      case 'create':
        return 'Created';
      case 'update':
        return 'Updated';
      case 'delete':
        return 'Deleted';
      default:
        return type;
    }
  };

  const getChangeTypeColor = (type: PromptHistoryEntry['changeType']) => {
    switch (type) {
      case 'create':
        return 'bg-green-100 text-green-700';
      case 'update':
        return 'bg-blue-100 text-blue-700';
      case 'delete':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold text-gray-900">Version History</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center gap-2 mb-4">
        <History className="w-5 h-5 text-gray-600" />
        <h3 className="font-semibold text-gray-900">Version History</h3>
      </div>

      {history.length === 0 ? (
        <p className="text-gray-500 text-sm py-4">
          No history yet. Changes will appear here after you save.
        </p>
      ) : (
        <div className="space-y-3 max-h-[600px] overflow-y-auto">
          {history.map((entry) => (
            <div
              key={entry.id}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              {/* Header */}
              <div
                className="px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {expandedId === entry.id ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${getChangeTypeColor(entry.changeType)}`}
                    >
                      {getChangeTypeLabel(entry.changeType)}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRestoreId(entry.id);
                    }}
                    disabled={restoring}
                    className="text-xs"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" />
                    Restore
                  </Button>
                </div>

                <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(entry.changedAt)}
                  </span>
                  {entry.changedByName && (
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {entry.changedByName}
                    </span>
                  )}
                </div>

                {entry.changeSummary && (
                  <p className="mt-1 text-xs text-gray-600">{entry.changeSummary}</p>
                )}
              </div>

              {/* Expanded Content */}
              {expandedId === entry.id && (
                <div className="px-4 py-3 border-t border-gray-200 bg-white">
                  <div className="space-y-3 text-sm">
                    {/* AI Prompt Preview */}
                    {entry.previousData.aiPrompt && (
                      <div>
                        <p className="font-medium text-gray-700 mb-1">AI Prompt:</p>
                        <pre className="bg-gray-50 p-2 rounded text-xs overflow-x-auto max-h-[200px] overflow-y-auto">
                          {entry.previousData.aiPrompt.slice(0, 500)}
                          {entry.previousData.aiPrompt.length > 500 && '...'}
                        </pre>
                      </div>
                    )}

                    {/* Chat Intro Preview */}
                    {entry.previousData.chatIntro && (
                      <div>
                        <p className="font-medium text-gray-700 mb-1">Chat Intro:</p>
                        <pre className="bg-gray-50 p-2 rounded text-xs overflow-x-auto max-h-[100px] overflow-y-auto">
                          {entry.previousData.chatIntro.slice(0, 300)}
                          {entry.previousData.chatIntro.length > 300 && '...'}
                        </pre>
                      </div>
                    )}

                    {/* Narrative Preview */}
                    {entry.previousData.narrative && (
                      <div>
                        <p className="font-medium text-gray-700 mb-1">Narrative:</p>
                        <div className="bg-gray-50 p-2 rounded text-xs space-y-1">
                          {entry.previousData.narrative.problem && (
                            <p>
                              <strong>Problem:</strong> {entry.previousData.narrative.problem}
                            </p>
                          )}
                          {entry.previousData.narrative.what && (
                            <p>
                              <strong>What:</strong> {entry.previousData.narrative.what}
                            </p>
                          )}
                          {entry.previousData.narrative.why && (
                            <p>
                              <strong>Why:</strong> {entry.previousData.narrative.why}
                            </p>
                          )}
                          {entry.previousData.narrative.how && (
                            <p>
                              <strong>How:</strong> {entry.previousData.narrative.how}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-gray-400">
                      Version {entry.previousData.version || 1}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={!!restoreId} onOpenChange={() => setRestoreId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore Previous Version?</AlertDialogTitle>
            <AlertDialogDescription>
              This will restore the prompt to the selected version. A new history entry will be
              created so you can undo this change if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore} disabled={restoring}>
              {restoring ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Restoring...
                </>
              ) : (
                'Restore'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
